import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  Sliders,
  PieChart,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  X,
  RefreshCw,
  Loader,
} from "lucide-react";
import mutualFundService from "@/services/api/mutual-fund.service"; // Adjust path if necessary
import apiService from "@/services/api/api.service"; // Adjust path if necessary
// Import the KycNotification component
import KycNotification from "@/components/static/kyc/kyc_notification"; // Adjust path if necessary

export default function MutualFundPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [mutualFunds, setMutualFunds] = useState([]);
  const [allFunds, setAllFunds] = useState([]); // Store all fetched funds
  const [searchTerm, setSearchTerm] = useState("");
  const [fundHouse, setFundHouse] = useState("");
  const [category, setCategory] = useState("");
  const [userPortfolio, setUserPortfolio] = useState(null);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [compareList, setCompareList] = useState([]);
  const [notification, setNotification] = useState(null);
  const [error, setError] = useState(null);
  const [isSearching, setIsSearching] = useState(false); // Track if user is searching

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Track initial load

  // Load initial data on component mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        await fetchMutualFunds();
        await fetchUserPortfolio();
        setIsInitialLoad(false);
      } catch (error) {
        console.error("Error loading initial data:", error);
        setIsInitialLoad(false);
      }
    };

    loadInitialData();
  }, []);

  // Handle page change when not in initial load
  useEffect(() => {
    if (!isInitialLoad) {
      updateDisplayedFunds();
    }
  }, [currentPage, pageSize, isInitialLoad]);

  // Debounced search implementation
  useEffect(() => {
    // Only search when term has content and is at least 3 characters
    if (searchTerm.length === 0) {
      // If search term is cleared, show all funds
      if (!isInitialLoad && isSearching) {
        clearSearch();
      }
      return;
    }

    const debounceTimer = setTimeout(() => {
      if (searchTerm.length >= 1) {
        handleSearch();
      }
    }, 300); // Reduced from 500ms to 300ms for quicker response

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  // Main function to fetch mutual funds - handles both search and regular fetching
  const fetchMutualFunds = async (isSearch = false) => {
    try {
      setError(null);
      setIsLoading(true);
      setIsSearching(isSearch);

      // Reset to first page when searching
      if (isSearch) {
        setCurrentPage(1);
      }

      // Prepare filters for search
      const filters = isSearch
        ? {
          name: searchTerm,
          fundHouse: fundHouse,
          category: category,
        }
        : {};

      // Use appropriate service method based on whether it's a search or initial load
      const response = await (isSearch
        ? mutualFundService.searchSchemes(filters)
        : mutualFundService.getAllSchemes({}));

      if (response.data.success) {
        // Handle nested data structure
        let fundData = [];
        if (Array.isArray(response.data.data)) {
          fundData = response.data.data;
        } else if (
          response.data.data &&
          Array.isArray(response.data.data.data)
        ) {
          fundData = response.data.data.data;
        } else {
          showNotification("Unexpected data format received", "error");
          fundData = [];
        }

        // Update total count
        setTotalCount(response.data.count || fundData.length);

        // Store all funds for client-side pagination
        setAllFunds(fundData);

        // Set the first page of mutual funds
        const paginatedFunds = fundData.slice(0, pageSize);
        setMutualFunds(paginatedFunds);

        // Check if we have more pages
        setHasMore(fundData.length > pageSize);
      }
    } catch (error) {
      setError("Failed to fetch mutual funds. Please try again later.");
      showNotification("Failed to fetch mutual funds", "error");
      // Reset to empty arrays on error to avoid displaying stale data
      setAllFunds([]);
      setMutualFunds([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Update displayed funds based on current page and pageSize
  const updateDisplayedFunds = () => {
    if (allFunds.length === 0) return;

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedFunds = allFunds.slice(startIndex, endIndex);
    setMutualFunds(paginatedFunds);
    setHasMore(endIndex < allFunds.length);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage < 1) return; // If trying to go below first page
    if (newPage > currentPage && !hasMore) return; // If trying to go beyond available data

    setCurrentPage(newPage);
  };

  // Fetch user's portfolio data
  const fetchUserPortfolio = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await apiService.getUserHoldings();

      if (response?.data?.success) {
        // Focus only on mutual fund portfolios
        const portfolioList = response.data.data || [];

        // Initialize portfolio data structure
        const portfolioData = {
          totalInvested: 0,
          currentValue: 0,
          overallReturn: 0,
          investments: [],
        };

        // Process only mutual fund portfolios
        for (const portfolio of portfolioList) {
          // Only process portfolios of type "MUTUAL FUND"
          if (
            portfolio.portfolioType === "MUTUAL FUND" &&
            portfolio.holdings &&
            Array.isArray(portfolio.holdings)
          ) {
            for (const holding of portfolio.holdings) {
              // Ensure holding is valid
              if (!holding) continue;

              // Create investment object for each holding
              const investment = {
                id: holding._id || `${portfolio._id}-${Math.random()}`,
                schemeName: holding.productName || "Unknown Fund",
                fundHouse: holding.fundHouse || "Unknown Provider",
                units: parseFloat(holding.quantity) || 0,
                investedAmount: parseFloat(holding.investmentValue) || 0,
                currentValue: parseFloat(holding.currentValue) || 0,
                navValue: parseFloat(holding.navValue) || 0,
                returns: 0,
              };

              // Calculate returns if we have valid values
              if (investment.investedAmount > 0) {
                investment.returns =
                  ((investment.currentValue - investment.investedAmount) /
                    investment.investedAmount) *
                  100;
              }

              // Add to totals
              portfolioData.totalInvested += investment.investedAmount;
              portfolioData.currentValue += investment.currentValue;

              // Add to investments array
              portfolioData.investments.push(investment);
            }
          }
        }

        // Calculate overall return
        if (portfolioData.totalInvested > 0) {
          portfolioData.overallReturn =
            ((portfolioData.currentValue - portfolioData.totalInvested) /
              portfolioData.totalInvested) *
            100;
        }

        // Set the processed portfolio data
        setUserPortfolio(portfolioData);
      } else {
        setUserPortfolio({
          totalInvested: 0,
          currentValue: 0,
          overallReturn: 0,
          investments: [],
        });
      }
    } catch (error) {
      showNotification("Unable to fetch your mutual fund portfolio", "error");
      setUserPortfolio({
        totalInvested: 0,
        currentValue: 0,
        overallReturn: 0,
        investments: [],
      });
    }
  };

  // Handle search function - triggers API search
  const handleSearch = async () => {
    if (searchTerm.trim().length === 0) {
      return await clearSearch();
    }
    await fetchMutualFunds(true);
  };

  // Refresh function to load all funds when no search results are found
  const refreshFunds = async () => {
    setSearchTerm("");
    setFundHouse("");
    setCategory("");
    setCurrentPage(1);
    await fetchMutualFunds(false);
    showNotification("All funds loaded successfully", "success");
  };

  // Clear search results and show all funds
  const clearSearch = async () => {
    // Reset search and filters
    setSearchTerm("");
    setFundHouse("");
    setCategory("");
    setCurrentPage(1);
    setIsLoading(true);
    setIsSearching(false);

    try {
      // Fetch all schemes without search filters
      const response = await mutualFundService.getAllSchemes({});

      if (response.data.success) {
        // Process response data
        let fundData = [];
        if (Array.isArray(response.data.data)) {
          fundData = response.data.data;
        } else if (
          response.data.data &&
          Array.isArray(response.data.data.data)
        ) {
          fundData = response.data.data.data;
        }

        // Update state with new data
        setTotalCount(response.data.count || fundData.length);
        setAllFunds(fundData);
        setMutualFunds(fundData.slice(0, pageSize));
        setHasMore(fundData.length > pageSize);
      }
    } catch (error) {
      setError("Failed to fetch mutual funds. Please try again later.");
      showNotification("Failed to fetch mutual funds", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle immediate search clearing from X button
  const handleClearSearch = () => {
    // Clear search term immediately
    setSearchTerm("");
    // The useEffect will trigger clearSearch()
  };

  // Navigate to scheme details page
  const viewSchemeDetails = (schemeCode) => {
    navigate(`/mutual-funds/${schemeCode}`);
  };

  // Add or remove fund from compare list
  const toggleCompare = (scheme) => {
    if (compareList.some((item) => item.schemeCode === scheme.schemeCode)) {
      setCompareList(
        compareList.filter((item) => item.schemeCode !== scheme.schemeCode)
      );
    } else {
      if (compareList.length < 3) {
        setCompareList([...compareList, scheme]);
      } else {
        showNotification(
          "You can compare up to 3 schemes at a time",
          "warning"
        );
      }
    }
  };

  // Show notification toast
  const showNotification = (message, type = "info") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Format numbers with commas
  const formatNumber = (num) => {
    if (num === undefined || num === null) return "N/A";
    return new Intl.NumberFormat("en-IN").format(num);
  };

  // Toggle advanced filters
  const toggleFilters = () => {
    setFiltersVisible(!filtersVisible);
  };

  // Render the notification component
  const renderNotification = () => {
    if (!notification) return null;

    const bgColor =
      notification.type === "success"
        ? "bg-green-100 border-green-500"
        : notification.type === "error"
          ? "bg-red-100 border-red-500"
          : notification.type === "warning"
            ? "bg-yellow-100 border-yellow-500"
            : "bg-blue-100 border-blue-500";

    return (
      <div
        className={`fixed bottom-4 right-4 p-4 rounded shadow-lg border-l-4 ${bgColor} z-50`}
      >
        <p>{notification.message}</p>
      </div>
    );
  };

  // Error display component
  const ErrorDisplay = ({ message }) => (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4">
      <div className="flex items-center">
        <AlertTriangle className="text-red-500 mr-2" size={20} />
        <p className="text-red-600">{message}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 mt-10 px-4 md:px-8 mb-8 sm:mb-0">
      {/* KYC Notification */}
      <KycNotification />

      {/* Header */}
      {/* <header className="bg-white shadow-sm">
        <div className="container mx-auto">
          <div className="flex mt-3">
            <button className="mr-4 pb-2 px-1 font-medium text-blue-600 border-b-2 border-blue-600">
              Explore
            </button>
            {userPortfolio && userPortfolio.investments.length > 0 && (
              <Link
                to="/mutual-funds/portfolio"
                className="mr-4 pb-2 px-1 font-medium text-gray-600 hover:text-blue-600"
              >
                My Portfolio
              </Link>
            )}
          </div>
        </div>
      </header> */}

      {/* Main Content */}
      <main className="container mx-auto">
        {error && <ErrorDisplay message={error} />}

        {/* Search Section - Centered and Smaller */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <h1 className="text-2xl font-bold text-gray-800">
            Mutual Fund Explorer
          </h1>
          <div className="flex flex-col items-center justify-center">
            <div className="relative w-full max-w-md mx-auto">
              <input
                type="text"
                placeholder="Search by fund name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-10 py-2 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
              <Search
                className="absolute left-3 top-2.5 text-gray-400"
                size={16}
              />
              {searchTerm && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Compare Section - Only show if funds are selected for comparison */}
        {compareList.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-800">
                Compare Funds ({compareList.length}/3)
              </h2>
              <button
                onClick={() => setCompareList([])}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear All
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {compareList.map((fund) => (
                <div
                  key={fund.schemeCode}
                  className="bg-gray-50 p-4 rounded-lg relative"
                >
                  <button
                    onClick={() => toggleCompare(fund)}
                    className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                  <p className="font-medium truncate" title={fund.schemeName}>
                    {fund.schemeName}
                  </p>
                </div>
              ))}
            </div>
            {compareList.length >= 2 && (
              <button
                onClick={() =>
                  navigate("/mutual-funds/compare", {
                    state: { funds: compareList },
                  })
                }
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200"
              >
                Compare Funds
              </button>
            )}
          </div>
        )}

        {/* Funds List */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <div>
              <h2 className="text-lg font-medium text-gray-800">
                Available Mutual Funds
              </h2>
              <p className="text-gray-500 text-sm">
                {totalCount > 0
                  ? `Showing ${(currentPage - 1) * pageSize + 1} - ${Math.min(
                    currentPage * pageSize,
                    totalCount
                  )} of ${totalCount} funds`
                  : `${mutualFunds.length} funds found`}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Page {currentPage}</span>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center p-16">
              <Loader className="animate-spin text-indigo-500 mb-4" size={36} />
            </div>
          ) : (
            <>
              {mutualFunds.length === 0 ? (
                <div className="p-8 text-center">
                  <div className="text-gray-500 mb-4">
                    No mutual funds found matching your criteria
                  </div>
                  {/* <button
                    onClick={refreshFunds}
                    className="flex items-center mx-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200"
                  >
                    <RefreshCw size={16} className="mr-2" />
                    Refresh and Show All Funds
                  </button> */}
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      <thead className="bg-gray-50 text-gray-700">
                        <tr>
                          <th className="px-6 py-3">Scheme Code</th>
                          <th className="px-6 py-3">Scheme Name</th>
                          <th className="px-6 py-3">ISIN Growth</th>
                          <th className="px-6 py-3">ISIN Div Reinvestment</th>
                          <th className="px-6 py-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mutualFunds.map((fund) => (
                          <tr
                            key={fund.schemeCode}
                            className="border-b hover:bg-gray-50"
                          >
                            <td className="px-6 py-4 font-medium">
                              {fund.schemeCode || "N/A"}
                            </td>
                            <td className="px-6 py-4">
                              {fund.schemeName || "N/A"}
                            </td>
                            <td className="px-6 py-4 text-gray-600">
                              {fund.isinGrowth || "N/A"}
                            </td>
                            <td className="px-6 py-4 text-gray-600">
                              {fund.isinDivReinvestment || "N/A"}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <button
                                onClick={() =>
                                  viewSchemeDetails(fund.schemeCode)
                                }
                                className="text-blue-600 hover:text-blue-800 font-medium"
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination controls */}
                  <div className="flex justify-between items-center p-4 border-t border-gray-200">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`flex items-center px-3 py-1 rounded ${
                        currentPage === 1
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-blue-600 hover:bg-blue-50"
                        }`}
                    >
                      <ChevronLeft size={16} className="mr-1" /> Previous
                    </button>
                    <div className="flex space-x-2">
                      {(() => {
                        const totalPages =
                          Math.ceil(totalCount / pageSize) || 1;
                        const maxVisiblePages = 5;
                        let startPage = 1;
                        let endPage = totalPages;

                        if (totalPages > maxVisiblePages) {
                          // Calculate the start and end page based on current page
                          const halfVisible = Math.floor(maxVisiblePages / 2);

                          if (currentPage <= halfVisible + 1) {
                            // We're near the beginning
                            endPage = maxVisiblePages;
                          } else if (currentPage >= totalPages - halfVisible) {
                            // We're near the end
                            startPage = totalPages - maxVisiblePages + 1;
                          } else {
                            // We're in the middle
                            startPage = currentPage - halfVisible;
                            endPage = currentPage + halfVisible;
                          }
                        }

                        // Generate page buttons
                        const pageButtons = [];
                        for (let i = startPage; i <= endPage; i++) {
                          pageButtons.push(
                            <button
                              key={i}
                              onClick={() => handlePageChange(i)}
                              className={`w-8 h-8 flex items-center justify-center rounded ${currentPage === i
                                  ? "bg-blue-600 text-white"
                                  : "text-gray-700 hover:bg-gray-100"
                                }`}
                            >
                              {i}
                            </button>
                          );
                        }

                        return pageButtons;
                      })()}
                    </div>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!hasMore}
                      className={`flex items-center px-3 py-1 rounded ${
                        !hasMore
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-blue-600 hover:bg-blue-50"
                        }`}
                    >
                      Next <ChevronRight size={16} className="ml-1" />
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Refresh Button - Show when user is in search mode but not during loading */}
        {isSearching && mutualFunds.length > 0 && !isLoading && (
          <div className="mt-4 flex justify-center">
            <button
              onClick={refreshFunds}
              className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition duration-200"
            >
              <RefreshCw size={16} className="mr-2" />
              Show All Funds
            </button>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 py-6">
        <div className="container mx-auto px-4 text-center text-gray-500 text-sm">
          <p className="mt-1">
            Mutual fund investments are subject to market risks. Please read all
            scheme related documents carefully before investing.
          </p>
        </div>
      </footer>

      {/* Notification Toast */}
      {renderNotification()}
    </div>
  );
}
